import { theme as antdTheme } from 'antd'
import { lightThemeColors, darkThemeColors } from '../configs/theme.colors'

/**
 * Get light mode Ant Design tokens
 * Uses lightThemeColors as single source of truth
 */
const getLightTokens = () => ({
    colorText: lightThemeColors.text,
    colorTextSecondary: lightThemeColors.secondaryText,
    colorTextTertiary: lightThemeColors.secondaryText,
    colorTextQuaternary: lightThemeColors.secondaryText,
    colorPrimary: lightThemeColors.primary,
    colorLink: lightThemeColors.primary,
    colorInfo: lightThemeColors.primary,
    colorInfoText: lightThemeColors.primary,
    colorSuccess: lightThemeColors.success,
    colorSuccessText: lightThemeColors.success,
    colorSuccessBg: lightThemeColors.successBg,
    colorError: lightThemeColors.error,
    colorErrorText: lightThemeColors.error,
    colorErrorBg: lightThemeColors.errorBg,
    // Warning: For Tags, colorWarning is used as text color, so use dark brown for contrast
    colorWarning: lightThemeColors.warningText, // #9B6803 - dark brown for readability
    colorWarningBg: lightThemeColors.warningBackground, // #fffbe6 - light yellow background
    colorWarningBorder: lightThemeColors.warning, // #FFBF00 - bright yellow border
})

/**
 * Get dark mode Ant Design tokens
 * Uses darkThemeColors as single source of truth
 */
const getDarkTokens = (colors) => ({
    colorPrimary: colors.primary,
    colorBgBase: colors.mainBg,
    colorTextBase: colors.txtWhite,
    colorBorder: colors.borderColor,
    colorBgContainer: colors.secondBg,
    colorBgElevated: colors.secondBg,
    colorFillSecondary: colors.secondBg,
    colorFillTertiary: colors.secondBg,
    colorFillQuaternary: colors.secondBg,
    colorIcon: colors.textPrimary,
    colorIconHover: colors.txtWhite,
    // Links use lighter blue for AAA text contrast
    colorLink: colors.primaryText,
    colorLinkHover: colors.senseColor,
    colorLinkActive: colors.primaryText,
    // These control input/form backgrounds
    controlItemBgActive: colors.mainBg,
    controlItemBgHover: colors.hoverBg,
    // Status colors - must match theme.colors.js
    colorSuccess: colors.success,
    colorSuccessText: colors.success,
    colorSuccessBg: colors.successBg,
    colorError: colors.error,
    colorErrorText: colors.error,
    colorErrorBg: colors.errorBg,
    colorWarning: colors.warning,
    colorWarningText: colors.warningText,
    colorWarningBg: colors.warningBg,
})

/**
 * Get dark mode Ant Design component tokens
 */
const getDarkComponents = (colors) => ({
    Typography: {
        colorText: colors.txtWhite,
        colorTextSecondary: colors.secondaryText,
        colorTextHeading: colors.txtWhite,
    },
    Input: {
        colorTextPlaceholder: colors.secondaryText,
        colorBgContainer: colors.secondBg,
        colorBgContainerDisabled: colors.mainBg,
        colorBorder: colors.borderColor,
        colorText: colors.textPrimary,
        activeBorderColor: colors.primary,
        hoverBorderColor: colors.primary,
        activeBg: colors.secondBg,
        hoverBg: colors.secondBg,
        addonBg: colors.secondBg,
        colorFillAlter: colors.secondBg,
        colorBgSpotlight: colors.secondBg,
    },
    Select: {
        colorTextPlaceholder: colors.secondaryText,
        colorBgContainer: colors.mainBg,
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorIcon: colors.textPrimary,
        colorIconHover: colors.txtWhite,
        colorBorder: colors.borderColor,
        optionSelectedBg: colors.activeBg,
        optionActiveBg: colors.hoverBg,
    },
    Button: {
        colorText: colors.textPrimary,
        colorIcon: colors.textPrimary,
        colorIconHover: colors.txtWhite,
        colorBorder: colors.borderColor,
        defaultColor: colors.textPrimary,
        defaultBorderColor: colors.borderColor,
    },
    Menu: {
        darkItemColor: colors.textPrimary,
        darkItemHoverColor: colors.txtWhite,
        darkItemSelectedColor: colors.primary,
        darkItemSelectedBg: colors.activeBg,
        darkSubMenuItemBg: 'transparent',
        darkItemBg: 'transparent',
        itemColor: colors.textPrimary,
        itemHoverColor: colors.txtWhite,
        itemSelectedColor: colors.primary,
    },
    Collapse: {
        headerBg: 'transparent',
        contentBg: 'transparent',
        colorText: colors.textPrimary,
        colorTextHeading: colors.textPrimary,
        colorIcon: colors.textPrimary,
    },
    Switch: {
        colorTextQuaternary: colors.textQuaternary,
    },
    Dropdown: {
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorBorderSecondary: colors.borderColor,
    },
    Drawer: {
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorBorderSecondary: colors.borderColor,
    },
    Popover: {
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorBorderSecondary: colors.borderColor,
    },
    Card: {
        colorBgContainer: colors.secondBg,
        colorText: colors.textPrimary,
        colorBorderSecondary: colors.borderColor,
    },
    TreeSelect: {
        colorBgContainer: colors.mainBg,
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorTextPlaceholder: colors.secondaryText,
        colorBorder: colors.borderColor,
        nodeSelectedBg: colors.activeBg,
        nodeHoverBg: colors.hoverBg,
    },
    Tree: {
        colorBgContainer: colors.secondBg,
        colorText: colors.textPrimary,
        nodeSelectedBg: colors.activeBg,
        nodeHoverBg: colors.hoverBg,
    },
    DatePicker: {
        colorBgContainer: colors.mainBg,
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorTextPlaceholder: colors.secondaryText,
        colorBorder: colors.borderColor,
        colorIcon: colors.textSecondary,
        colorIconHover: colors.textPrimary,
    },
    Cascader: {
        colorBgContainer: colors.mainBg,
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorTextPlaceholder: colors.secondaryText,
        colorBorder: colors.borderColor,
        optionSelectedBg: colors.activeBg,
    },
    AutoComplete: {
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
    },
    Modal: {
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        headerBg: colors.secondBg,
        contentBg: colors.secondBg,
        footerBg: colors.secondBg,
    },
    Tooltip: {
        colorBgSpotlight: colors.secondBg,
        colorTextLightSolid: colors.textPrimary,
    },
    Table: {
        colorBgContainer: colors.secondBg,
        colorText: colors.textPrimary,
        colorTextHeading: colors.textPrimary,
        headerBg: colors.mainBg,
        headerColor: colors.textPrimary,
        rowHoverBg: colors.hoverBg,
        rowSelectedBg: colors.activeBg,
        rowSelectedHoverBg: colors.activeBgHover,
        rowExpandedBg: colors.mainBg,
        borderColor: colors.borderColor,
        filterDropdownBg: colors.secondBg,
        bodySortBg: colors.mainBg,
        headerSortActiveBg: colors.mainBg,
        headerSortHoverBg: colors.hoverBg,
        fixedHeaderSortActiveBg: colors.mainBg,
    },
    Popconfirm: {
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
    },
    ColorPicker: {
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorBorder: colors.borderColor,
    },
    InputNumber: {
        colorBgContainer: colors.mainBg,
        colorText: colors.textPrimary,
        colorBorder: colors.borderColor,
        handleBg: colors.secondBg,
    },
    Mentions: {
        colorBgContainer: colors.mainBg,
        colorBgElevated: colors.secondBg,
        colorText: colors.textPrimary,
        colorBorder: colors.borderColor,
    },
    Statistic: {
        titleColor: colors.secondaryText,
        contentColor: colors.txtWhite,
    },
    Tabs: {
        itemColor: colors.secondaryText,
        itemHoverColor: colors.primary,
        itemActiveColor: colors.txtWhite,
        itemSelectedColor: colors.txtWhite,
    },
})

/**
 * Get Ant Design theme configuration based on mode
 * @param {string} mode - 'light' or 'dark'
 * @param {Object} colors - Theme colors from getThemeColors()
 * @returns {Object} Ant Design theme configuration
 */
export const getAntdThemeConfig = (mode, colors) => {
    const isDarkMode = mode === 'dark'
    
    return {
        algorithm: isDarkMode ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
        // Light mode: minimal overrides for brand consistency
        // Dark mode: full custom token set
        token: isDarkMode ? getDarkTokens(colors) : getLightTokens(),
        // Light mode: use Ant Design component defaults
        // Dark mode: apply custom component tokens
        components: isDarkMode ? getDarkComponents(colors) : {}
    }
}
